import React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import AppLayout from "@amzn/awsui-components-react/polaris/app-layout";

import { DEFAULT_LEFT_NAV_PATH } from "./shellRoutes";
import { LeftNav } from "./LeftNav";
import { AppBreadcrumbs } from "./AppBreadcrumbs";
import { FilterBar } from "../filterBar/FilterBar";
import { PorAsinListTable } from "../porAsinList/PorAsinListTable";
import { BaselineTable } from "../dateBaseline/baselineTable";
import { AsinValidationTable } from "../validation/asinValidationTable";
import { OneClickLaunchTable } from "../oneClickLaunch/oneClickLaunchTable";

/**
 * Root component of the App Shell mounted at the V2 Route (`/v2`).
 *
 * The App Shell wraps a Cloudscape {@link AppLayout} in
 * `<BrowserRouter basename="/v2">` and populates the three primary
 * `AppLayout` slots as follows:
 *
 * - `navigation`: {@link LeftNav} — the persistent left navigation panel
 *   with the four primary items ("ASIN Search", "ASIN Date Baseline",
 *   "Validation", "Launch") and the inert Wiki Link (task 4).
 * - `breadcrumbs`: {@link AppBreadcrumbs} — the trail rendered above the
 *   routed content on every primary Left Nav path. The full
 *   implementation is task 6; a placeholder module is mounted here so
 *   the slot wiring is in place.
 * - `content`: the {@link FilterBar} rendered above a `<Routes>` block
 *   that dispatches on the router-relative path (i.e. WITHOUT the `/v2`
 *   prefix, because the router's `basename` already accounts for it).
 *
 * The `<Routes>` block enumerates every V2 Route the design calls for:
 *
 * | Router path   | Element                                                 |
 * | ------------- | ------------------------------------------------------- |
 * | `/`           | `<Navigate to="/search" replace/>` — default       |
 * | `/search`     | Existing `PorAsinListTable`                             |
 * | `/baseline`   | Existing `BaselineTable`                                |
 * | `/validation` | Existing `AsinValidationTable` (Phase 1 safety net —    |
 * |               |  Phase 2 replaces this with `ValidationDashboard`)      |
 * | `/launch`     | Existing `OneClickLaunchTable`                          |
 * | `*`           | `<Navigate to="/search" replace/>` — fallback      |
 *
 * The four existing view components are imported and mounted here. Each
 * view already reads and writes filter state through the existing
 * `generateQuery` Redux slice, so mounting them under the App Shell
 * satisfies "shared Filter Bar backed by the Global Search State"
 * without introducing a second slice.
 *
 * The `FilterBar` is mounted **above** `<Routes>` so it renders on every
 * primary Left Nav path. It is not scoped to any single
 * route element because task 3's route redirects (`/` → `/search`, and
 * the wildcard fallback) already guarantee the user is always on one of
 * the four primary paths, and rendering the bar unconditionally lets it
 * survive intra-shell navigation without unmount/remount churn.
 *
 * Cloudscape's `AppLayout` is instructed to hide the tools panel
 * (`toolsHide={true}`) because the App Shell's design does not use it,
 * and to accept sticky notifications (`stickyNotifications={true}`) to
 * match the existing legacy App's behavior. The `contentType` is
 * intentionally left at its default because the App Shell renders
 * varied content (widgets, tables, breadcrumbs) rather than a single
 * table layout.
 *
 */
export const AppShell: React.FC = () => {
  return (
    <BrowserRouter basename="/v2">
      <AppLayout
        data-testid="app-shell-layout"
        navigation={<LeftNav />}
        breadcrumbs={<AppBreadcrumbs />}
        toolsHide={true}
        stickyNotifications={true}
        content={
          <div data-testid="app-shell-content">
            <FilterBar />
            <Routes>
              <Route
                path="/"
                element={<Navigate to={DEFAULT_LEFT_NAV_PATH} replace />}
              />
              <Route path="/search" element={<PorAsinListTable />} />
              <Route path="/baseline" element={<BaselineTable />} />
              <Route path="/validation" element={<AsinValidationTable />} />
              <Route path="/launch" element={<OneClickLaunchTable />} />
              <Route
                path="*"
                element={<Navigate to={DEFAULT_LEFT_NAV_PATH} replace />}
              />
            </Routes>
          </div>
        }
      />
    </BrowserRouter>
  );
};

export default AppShell;
